import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Unlock, ArrowDownRight, ArrowUpRight, History } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatIDR, formatDate, formatApiError } from "../lib/api";
import { SkeletonRow, EmptyState } from "../components/Skeleton";
import { Button } from "../components/ui/button";

export default function WalletLedgerPage() {
    const navigate = useNavigate();
    const [ledger, setLedger] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setLedger(null); setError("");
        Promise.all([api.get("/wallets/me", { signal: controller.signal }), api.get("/wallets/me/ledger", { params: { skip: page * 20, take: 20 }, signal: controller.signal })])
            .then(([balance, history]) => { setWallet(balance.data); setLedger(history.data.items || []); setTotal(history.data.total || 0); })
            .catch((err) => { if (!controller.signal.aborted) { setError(formatApiError(err)); setLedger([]); } });
        return () => controller.abort();
    }, [page, revision]);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <Button 
                    aria-label="Kembali"
                    variant="ghost" 
                    size="icon" 
                    onClick={() => navigate(-1)}
                    className="rounded-full"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="font-heading font-bold text-xl tracking-tight">Mutasi Saldo</h1>
            </div>

            {wallet && <section className="rounded-2xl bg-brand-deep p-5 text-white"><p className="text-sm text-white/75">Saldo tersedia</p><p className="font-heading text-2xl font-bold mt-1">{formatIDR(wallet.balance)}</p><div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-xs text-white/80"><span>Ditahan: {formatIDR(wallet.lockedBalance)}</span><span>Menunggu: {formatIDR(wallet.pendingBalance)}</span></div><Button onClick={() => navigate("/deposit")} className="mt-4 bg-brand-green hover:bg-brand-greenDark text-white">Top Up Saldo</Button></section>}
            <div className="space-y-3">
                {error ? <div role="alert" className="text-sm text-destructive">{error}<Button variant="outline" className="ml-2" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button></div> : ledger === null ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : ledger.length === 0 ? (
                    <EmptyState
                        title="Belum ada mutasi"
                        description="Riwayat perubahan saldo Anda akan muncul di sini."
                        icon={<History className="h-7 w-7 text-muted-foreground" />}
                    />
                ) : (
                    ledger.map((item, i) => (
                        <LedgerItem key={item.id} item={item} index={i} />
                    ))
                )}
            </div>
            <div className="flex items-center justify-between gap-2"><Button variant="outline" disabled={page === 0 || ledger === null} onClick={() => setPage((value) => value - 1)}>Sebelumnya</Button><span className="text-xs text-muted-foreground">{total} mutasi</span><Button variant="outline" disabled={(page + 1) * 20 >= total || ledger === null} onClick={() => setPage((value) => value + 1)}>Berikutnya</Button></div>
        </div>
    );
}

function LedgerItem({ item, index }) {
    const isPositive = BigInt(item.balanceAfter ?? 0) > BigInt(item.balanceBefore ?? 0);
    const isNegative = BigInt(item.balanceAfter ?? 0) < BigInt(item.balanceBefore ?? 0);
    
    let Icon = History;
    let colorClass = "text-muted-foreground bg-muted";
    let amountPrefix = isPositive ? "+" : isNegative ? "-" : "";

    if (item.type === "LOCK") {
        Icon = Lock;
        colorClass = "text-amber-500 bg-amber-500/10";
        amountPrefix = "-";
    } else if (item.type === "UNLOCK") {
        Icon = Unlock;
        colorClass = "text-blue-500 bg-blue-500/10";
        amountPrefix = "+";
    } else if (item.type === "DEBIT") {
        Icon = ArrowDownRight;
        colorClass = "text-rose-500 bg-rose-500/10";
        amountPrefix = "-";
    } else if (item.type === "CREDIT") {
        Icon = ArrowUpRight;
        colorClass = "text-emerald-500 bg-emerald-500/10";
        amountPrefix = "+";
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.02 }}
            className="p-4 rounded-2xl bg-card border border-border flex items-start gap-4"
        >
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <Icon className="h-5 w-5" />
            </div>
            
            <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                    <div className="font-semibold text-sm leading-tight truncate">
                        {item.description || item.type}
                    </div>
                    <div className={`font-heading font-bold text-sm whitespace-nowrap ${isPositive ? "text-emerald-500" : isNegative ? "text-rose-500" : ""}`}>
                        {amountPrefix}{formatIDR(item.amount)}
                    </div>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-between items-center mt-2">
                    <div className="text-xs text-muted-foreground">
                        {formatDate(item.createdAt)}
                    </div>
                    <div className="text-xs font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                        Saldo: {formatIDR(item.balanceAfter)}
                    </div>
                </div>

                {(Number(item.lockedAfter) > 0 || Number(item.lockedBefore) > 0) && (
                    <div className="mt-1 text-xs text-muted-foreground flex gap-2">
                        <span>Ditahan: {formatIDR(item.lockedAfter)}</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
